import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CircularProgress, Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { useAddDriveTypeMutation, useGetCategoriesQuery, useUpdateDriveTypeMutation } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedItem?: any;
}

export default function AddDriveType({
  open,
  onClose,
  setOpen,
  selectedItem
}: ModalProps) {

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">("");
  const [name, setName] = useState("");
  const [errors, setErrors] = useState<any>({});

  const { data: categoriesData } = useGetCategoriesQuery(
    { search: "", page: 1, limit: 100, type: selectedVehicleType },
    { skip: !selectedVehicleType }
  );
  const [addDriveType, { isLoading: isAdding }] = useAddDriveTypeMutation();
  const [updateDriveType, { isLoading: isUpdating }] = useUpdateDriveTypeMutation();

  useEffect(() => {
    if (selectedItem && open) {
      setName(selectedItem.name || "");

      // Robust extraction of category IDs
      let catIds: string[] = [];
      const cats = selectedItem.categoryId || selectedItem.category || selectedItem.categories;

      if (Array.isArray(cats) && cats.length > 0) {
        catIds = cats.map((c: any) => (typeof c === 'object' && c !== null) ? (c._id || c.id) : c);
      } else if (cats) {
        catIds = [(typeof cats === 'object' && cats !== null) ? (cats._id || cats.id) : cats];
      }

      setSelectedCategories(catIds.filter(Boolean));

      const vType = selectedItem.type || selectedItem.vehicleType;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setErrors({});
    } else if (!selectedItem && open) {
      setName("");
      setSelectedCategories([]);
      setSelectedVehicleType("");
      setErrors({});
    }
  }, [selectedItem, open]);

  const handleCategoryChange = (event: any) => {
    const value = event.target.value;
    const categories = categoriesData?.data?.data || [];

    if (value.includes("all")) {
      if (selectedCategories.length === categories.length) {
        setSelectedCategories([]);
      } else {
        setSelectedCategories(categories.map((cat: any) => cat._id));
      }
    } else {
      setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    }
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleVehicleTypeChange = (event: any) => {
    const value = event.target.value as number;
    setSelectedVehicleType(value);
    setSelectedCategories([]); // Reset categories when vehicle type changes
    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
  };

  const handleNameChange = (e: any) => {
    const value = e.target.value;
    if (value.length <= 40) {
      setName(value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Drive Type is required / نوع الدفع مطلوب";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required / مطلوب فئة واحدة على الأقل";
      isValid = false;
    }
    if (!selectedVehicleType) {
      tempErrors.vehicleType = "Vehicle type is required / نوع المركبة مطلوب";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    try {
      const payload = {
        name,
        categoryId: selectedCategories,
        type: Number(selectedVehicleType)
      };

      if (selectedItem) {
        await updateDriveType({ id: selectedItem._id, ...payload }).unwrap();
        toast.success("Drive Type updated successfully / تم تحديث نوع الدفع بنجاح");
      } else {
        await addDriveType(payload).unwrap();
        toast.success("Drive Type added successfully / تمت إضافة نوع الدفع بنجاح");
      }
      setOpen(false);
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || "Something went wrong / حدث خطأ ما");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{selectedItem ? "Edit Drive Type / تعديل نوع الدفع" : "Add Drive Type / إضافة نوع دفع"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close / إغلاق"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="control_group">
              <label htmlFor="drive-type-name">Drive Type / نوع الدفع</label>
              <TextField
                id="drive-type-name"
                placeholder="Enter Type (e.g., FWD, AWD) / أدخل النوع (مثال: FWD، AWD)"
                fullWidth
                hiddenLabel
                value={name}
                onChange={handleNameChange}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 40 }}
              />
            </div>

            <div className="control_group">
              <label htmlFor="VehicleType">Vehicle Type / نوع المركبة</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                value={selectedVehicleType}
                onChange={handleVehicleTypeChange}
                renderValue={(selected) => {
                  if (!selected) return "Select Vehicle Type / اختر نوع المركبة";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>Select Vehicle Type / اختر نوع المركبة</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
            </div>

            <div className="control_group">
              <label htmlFor="Category">Category / الفئة</label>
              <Select
                multiple
                fullWidth
                hiddenLabel
                value={selectedCategories}
                onChange={handleCategoryChange}
                renderValue={(selected) => {
                  const selectedIds = (selected as string[]) || [];
                  if (selectedIds.length === 0) return selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً";

                  return selectedIds.map(id => {
                    // 1. Try to find in loaded categories list
                    const cat = categoriesData?.data?.data?.find((c: any) => c._id === id);
                    if (cat?.name) return cat.name;

                    // 2. Search through all potential object fields in selectedItem
                    const sources = [selectedItem?.categoryId, selectedItem?.category, selectedItem?.categories];
                    for (const source of sources) {
                      const items = Array.isArray(source) ? source : [source];
                      const found = items.find((c: any) => c && typeof c === 'object' && (c._id === id || c.id === id));
                      if (found?.name) return found.name;
                    }

                    // 3. Try index matching with categoryName array if available
                    if (selectedItem?.categoryName) {
                      const nameArray = Array.isArray(selectedItem.categoryName) ? selectedItem.categoryName : [selectedItem.categoryName];
                      const cats = selectedItem?.categoryId || selectedItem?.category || selectedItem?.categories;
                      const catsArray = Array.isArray(cats) ? cats : [cats];

                      const index = catsArray.findIndex((c: any) => (typeof c === 'object' ? (c._id === id || c.id === id) : c === id));
                      if (index !== -1 && nameArray[index]) return nameArray[index];
                    }

                    return id;
                  }).join(", ");
                }}
                disabled={!selectedVehicleType}
                error={!!errors.category}
              >
                <MenuItem value="" disabled>
                  {selectedVehicleType ? "Select Categories / اختر الفئات" : "Select Vehicle Type First / اختر نوع المركبة أولاً"}
                </MenuItem>
                {categoriesData?.data?.data?.length > 0 && (
                  <MenuItem value="all">
                    <em>{selectedCategories.length === categoriesData.data.data.length ? "Deselect All / إلغاء تحديد الكل" : "Select All / تحديد الكل"}</em>
                  </MenuItem>
                )}
                {categoriesData?.data?.data?.map((category: any) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={selectedCategories.includes(category._id)} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.category}</div>}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "Save / حفظ"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
